import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Trophy, TrendingUp, TrendingDown, Info, Flame } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useGroupPoints } from "@/hooks/useGroupPoints";

interface LeaderboardEntry {
  playerId: string;
  playerName: string;
  totalGames: number;
  totalBuyins: number;
  netProfit: number;
  winRate: number;
  points: number;
  winStreak: number;
}

interface GroupLeaderboardProps {
  groupId: string;
}

export const GroupLeaderboard = ({ groupId }: GroupLeaderboardProps) => {
  const [players, setPlayers] = useState<{ id: string; name: string }[] | null>(null);
  const { data: points, isLoading: pointsLoading } = useGroupPoints(groupId);

  useEffect(() => {
    const load = async () => {
      try {
        const { data, error } = await supabase
          .from('players')
          .select('id, name, auth_user_id')
          .eq('group_id', groupId);
        if (error) throw error;
        const withNames = await Promise.all((data || []).map(async (player) => {
          if (player.auth_user_id) {
            const { data: profile } = await supabase
              .from('profiles')
              .select('display_name')
              .eq('id', player.auth_user_id)
              .maybeSingle();
            return { id: player.id, name: profile?.display_name || player.name };
          }
          return { id: player.id, name: player.name };
        }));
        setPlayers(withNames);
      } catch (error) {
        console.error('Error fetching leaderboard data:', error);
        setPlayers([]);
      }
    };
    load();
  }, [groupId]);

  if (players === null || pointsLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const entries: LeaderboardEntry[] = players.map((p) => {
    const s = points?.byPlayer[p.id];
    const totalGames = s?.gamesCounted || 0;
    return {
      playerId: p.id,
      playerName: p.name,
      totalGames,
      totalBuyins: s?.totalBuyins || 0,
      netProfit: s?.netProfit || 0,
      winRate: totalGames > 0 ? ((s?.wins || 0) / totalGames) * 100 : 0,
      points: s?.total || 0,
      winStreak: s?.currentWinStreak || 0,
    };
  });
  const ranked = entries.filter(e => e.totalGames > 0)
    .sort((a, b) => b.points - a.points || b.netProfit - a.netProfit);
  const unranked = entries.filter(e => e.totalGames === 0);
  const leaderboard = [...ranked, ...unranked];

  if (leaderboard.length === 0) {
    return (
      <Card>
        <CardContent className="text-center py-8">
          <Trophy className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">No Data Available</h3>
          <p className="text-muted-foreground">
            Play and settle some games to see the leaderboard.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Trophy className="h-5 w-5 mr-2" />
            Group Leaderboard
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  aria-label="How points are calculated"
                  className="ml-2 p-1 rounded-full text-muted-foreground hover:text-foreground"
                >
                  <Info className="h-4 w-4" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="text-sm space-y-2 font-normal">
                <p className="font-semibold">How points work</p>
                <ul className="list-disc pl-4 space-y-1">
                  <li>Win +5, loss -5, draw 0.</li>
                  <li>From the 3rd consecutive win, each win is worth 10.</li>
                  <li>A loss resets the streak.</li>
                  <li>A draw does not affect the streak.</li>
                  <li>Ranked by total points; ties broken by net profit.</li>
                  <li>Settled games only.</li>
                </ul>
                <p className="text-muted-foreground">
                  Example: W, W, D, W, L, W = 5 + 5 + 0 + 10 - 5 + 5 = 20
                </p>
              </PopoverContent>
            </Popover>
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Ranked by points - settled games only
          </p>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {leaderboard.map((entry, index) => {
              const isRanked = entry.totalGames > 0;
              return (
                <div
                  key={entry.playerId}
                  className="flex items-center justify-between p-4 bg-muted/50 rounded-lg"
                >
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary text-primary-foreground text-sm font-bold">
                      {isRanked ? index + 1 : '-'}
                    </div>
                    <div>
                      <p className="font-medium flex items-center">
                        {entry.playerName}
                        {entry.winStreak >= 3 && (
                          <Flame className="h-4 w-4 ml-1 text-orange-500" aria-label={`${entry.winStreak} win streak`} />
                        )}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {entry.totalGames} games • {entry.winRate.toFixed(1)}% win rate
                      </p>
                    </div>
                  </div>

                  <div className="text-right">
                    <p className={`text-xl font-bold ${
                      !isRanked || entry.points === 0
                        ? 'text-muted-foreground'
                        : entry.points > 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {isRanked ? `${entry.points > 0 ? '+' : ''}${entry.points} pts` : '-'}
                    </p>
                    <div className="flex items-center justify-end space-x-2">
                      {entry.netProfit >= 0 ? (
                        <TrendingUp className="h-4 w-4 text-green-500" />
                      ) : (
                        <TrendingDown className="h-4 w-4 text-red-500" />
                      )}
                      <span className={`font-bold ${entry.netProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {entry.netProfit >= 0 ? '+' : ''}₹{entry.netProfit.toFixed(2)}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Total played: ₹{entry.totalBuyins.toFixed(2)}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
